import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useRsvp } from '@/src/modules/rsvp';

interface RSVPBabySectionProps {
  invitationId: string | null;
  isLive: boolean;
  language: 'en' | 'mr';
}

export const RSVPBabySection: React.FC<RSVPBabySectionProps> = ({ invitationId, isLive, language }) => {
  const [name, setName] = useState('');
  const [attending, setAttending] = useState<boolean | null>(null);
  const [dietaryPrefs, setDietaryPrefs] = useState('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const isSubmitting = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || attending === null || isSubmitting) return;
    await submit({
      guestName: name.trim(),
      status: attending ? 'ACCEPTED' : 'DECLINED',
      headcount: 1,
      message: dietaryPrefs.trim() ? `Dietary: ${dietaryPrefs.trim()}`.slice(0, 500) : undefined,
    });
  };

  if (state === 'success') {
    return (
      <section className="relative py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-pink-400 rounded-full flex items-center justify-center animate-bounce">
              <CheckCircle2 size={40} className="text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-black text-gray-800 mb-4">
            {language === 'mr' ? 'धन्यवाद!' : 'Thank You!'}
          </h2>
          <p className="text-gray-600 text-lg">
            {language === 'mr'
              ? 'आपकी RSVP सफलतापूर्वक प्राप्त हुई। शावर में आपकी उपस्थिति के लिए उत्सुक हैं!'
              : "We're excited to see you at the baby shower!"}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 px-4 bg-gradient-to-b from-pink-50 to-yellow-50">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-pink-500 mb-12">
          {language === 'mr' ? '🎁 RSVP करा' : '🎁 RSVP Now'}
        </h2>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8 border-4 border-yellow-300 space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              {language === 'mr' ? 'नाव' : 'Name'} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border-2 border-yellow-300 rounded-lg px-4 py-3 font-semibold focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20"
            />
          </div>

          <div>
            <p className="text-sm font-bold text-gray-700 mb-4">
              {language === 'mr' ? 'क्या आप आ सकते हैं?' : 'Can you attend?'} *
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setAttending(true)}
                className={`py-4 px-6 rounded-lg font-bold transition-all ${
                  attending === true
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white shadow-lg'
                    : 'bg-gray-100 border-2 border-gray-300 text-gray-700 hover:border-yellow-400'
                }`}
              >
                ✓ {language === 'mr' ? 'होय' : 'Yes'}
              </button>
              <button
                type="button"
                onClick={() => setAttending(false)}
                className={`py-4 px-6 rounded-lg font-bold transition-all ${
                  attending === false
                    ? 'bg-gradient-to-r from-pink-400 to-rose-400 text-white shadow-lg'
                    : 'bg-gray-100 border-2 border-gray-300 text-gray-700 hover:border-pink-400'
                }`}
              >
                ✕ {language === 'mr' ? 'नहीं' : 'No'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              {language === 'mr' ? 'आहार की प्राथमिकताएं' : 'Dietary Preferences'}
            </label>
            <textarea
              value={dietaryPrefs}
              onChange={(e) => setDietaryPrefs(e.target.value)}
              placeholder={language === 'mr' ? 'बताएं...' : 'Let us know...'}
              rows={2}
              className="w-full border-2 border-yellow-300 rounded-lg px-4 py-3 font-semibold focus:border-pink-400 resize-none"
            />
          </div>

          {state === 'error' && errorMessage && (
            <p className="text-sm text-center font-semibold text-red-500">{errorMessage}</p>
          )}

          <button
            type="submit"
            disabled={attending === null || isSubmitting}
            className="w-full bg-gradient-to-r from-yellow-400 to-pink-400 text-white font-bold py-4 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🎁 {isSubmitting
              ? (language === 'mr' ? 'सबमिट करत आहे...' : 'Submitting...')
              : (language === 'mr' ? 'RSVP सबमिट करा' : 'Submit RSVP')}
          </button>
        </form>
      </div>
    </section>
  );
};
